import {
  CharityVettingUrlType,
  DonationPreferencesEnum,
} from 'features/charity-vetting/types/charityVettingTypes';
import { z } from 'zod';

import esMsg from './messages';

import type {
  PageSchemaFactory,
  PageSchemaMap,
} from 'features/charity-vetting/common-flow/validation/pageSchemaRegistry';

/**
 * Per-page Zod schemas for the ES vetting flow. CIF is a 9-char fiscal id;
 * RGSEAA is required only when the charity declares it has one (hasRgseaa).
 */

const wrap = (charityOnboarding: z.ZodTypeAny) => z.looseObject({ charityOnboarding });

const toStr = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const isFoodFlow = (donationPreferences: unknown) =>
  donationPreferences === DonationPreferencesEnum.ONLY_FOOD ||
  donationPreferences === DonationPreferencesEnum.FOOD_AND_NON_FOOD;

const cifPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z.looseObject({ cif: z.unknown().optional() }).superRefine((data, ctx) => {
      if (toStr(data.cif).length !== 9) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(esMsg.charityVettingCifRequiredES),
          path: ['cif'],
        });
      }
    }),
  );

const charityInformationPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ charityType: z.string().min(1) }));

const donationCertificatePageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ donationCertificate: z.boolean() }));

// rgseaa-question page — hasRgseaa must be answered in food flows
const rgseaaQuestionPageSchema: PageSchemaFactory = () =>
  wrap(
    z
      .looseObject({
        donationPreferences: z.string().optional(),
        hasRgseaa: z.boolean().optional(),
      })
      .superRefine((data, ctx) => {
        if (isFoodFlow(data.donationPreferences) && typeof data.hasRgseaa !== 'boolean') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'hasRgseaa-required',
            path: ['hasRgseaa'],
          });
        }
      }),
  );

// rgseaa page — required only when hasRgseaa is true
const rgseaaPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        hasRgseaa: z.boolean().optional(),
        rgseaa: z.unknown().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.hasRgseaa === true && !toStr(data.rgseaa)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(esMsg.charityVettingRgseaaRequiredES),
            path: ['rgseaa'],
          });
        }
      }),
  );

export const esPageSchemas: PageSchemaMap = {
  [CharityVettingUrlType.CIF]: cifPageSchema,
  [CharityVettingUrlType.REGISTRATION_DETAILS]: charityInformationPageSchema,
  [CharityVettingUrlType.DONATION_CERTIFICATE]: donationCertificatePageSchema,
  [CharityVettingUrlType.RGSEAA_QUESTION]: rgseaaQuestionPageSchema,
  [CharityVettingUrlType.RGSEAA]: rgseaaPageSchema,
};
